import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Paper, Typography, Chip, Card, CardContent, Grid, Button,
  TextField, Alert, Tooltip, MenuItem, Checkbox, FormControlLabel,
  List, ListItem, ListItemText, Stack, IconButton, InputAdornment
} from '@mui/material';
import {
  LocalOffer as DiscountIcon, CheckCircle as CheckCircleIcon,
  Edit as EditIcon, Delete as DeleteIcon,
  People as PeopleIcon, Security as SecurityIcon,
  ArrowBack as ArrowBackIcon, Search as SearchIcon, Close as CloseIcon
} from '@mui/icons-material';
import GenericPage, { useDefaultViewMode } from '../../Commun/components/GenericPage';
import RootDialogPage from '../../Commun/components/RootDialogPage';
import { blobticMessage } from '../../Commun/components/BlobticMessage';
import GenericTable from '../../Commun/components/GenericTable';
import { academyStore } from '../services/academyStore';
import { AcademyDiscount, ValidatorRole } from '../types/academy';
import { useLanguage } from '../../Commun/contexts/LanguageContext';

const ENTITY_KEY = 'Blobtic_Academy,academy_discount';
const ACTION_SUFFIX = '';
const PRIMARY_KEY = 'DSC_IDF';

const dtoToDiscount = (dto: any): AcademyDiscount => ({
  id: dto.DSC_IDF || dto.id,
  dsc_uidf: dto.DSC_UIDF || dto.dsc_uidf,
  dos_idf: dto.DOS_IDF ? Number(dto.DOS_IDF) : undefined,
  code: dto.DSC_CODE || dto.code || '',
  name: dto.DSC_NAME || dto.name || '',
  percentage: Number(dto.DSC_PERCENTAGE ?? dto.percentage ?? 0),
  requires_validation: dto.DSC_REQUIRES_VALIDATION != null ? Boolean(dto.DSC_REQUIRES_VALIDATION) : Boolean(dto.requires_validation),
  validator_role: dto.DSC_VALIDATOR_ROLE || dto.validator_role || 'Admin',
  is_auto_applied: dto.DSC_IS_AUTO_APPLIED != null ? Boolean(dto.DSC_IS_AUTO_APPLIED) : Boolean(dto.is_auto_applied),
  min_courses: dto.DSC_MIN_COURSES != null ? Number(dto.DSC_MIN_COURSES) : (dto.min_courses ?? 1),
  description: dto.DSC_DESCRIPTION || dto.description || '',
  is_active: dto.DSC_IS_ACTIVE != null ? Boolean(dto.DSC_IS_ACTIVE) : (dto.is_active ?? true),
  user_lib: dto.USER_LIB || dto.user_lib || undefined,
  last_updt: dto.LAST_UPDT
    ? (typeof dto.LAST_UPDT === 'string' ? dto.LAST_UPDT : new Date(dto.LAST_UPDT).toLocaleString('fr-DZ'))
    : (dto.last_updt || undefined),
});

const discountToDto = (d: Partial<AcademyDiscount>): Record<string, any> => ({
  DSC_IDF: (d as any).DSC_IDF ?? d.id,
  DSC_CODE: d.code || '',
  DSC_NAME: d.name || '',
  DSC_PERCENTAGE: d.percentage ?? 0,
  DSC_REQUIRES_VALIDATION: d.requires_validation ? 1 : 0,
  DSC_VALIDATOR_ROLE: d.validator_role || 'Admin',
  DSC_IS_AUTO_APPLIED: d.is_auto_applied ? 1 : 0,
  DSC_MIN_COURSES: d.min_courses ?? 1,
  DSC_DESCRIPTION: d.description || '',
  DSC_IS_ACTIVE: d.is_active != null ? (d.is_active ? 1 : 0) : 1,
  ...(d.user_lib ? { USER_LIB: d.user_lib } : {}),
});

const emptyDiscount = (): Partial<AcademyDiscount> => ({
  code: 'NEW_' + Math.floor(100 + Math.random() * 900),
  name: '',
  percentage: 10,
  requires_validation: false,
  validator_role: 'Admin',
  is_auto_applied: false,
  min_courses: 1,
  description: '',
  is_active: true
});

export const DiscountsPage: React.FC = () => {
  const { t } = useLanguage();
  const [, setTick] = useState(0);

  // View state: 'list' (default) or 'form' (new/edit)
  const [view, setView] = useState<'list' | 'form'>('list');

  // Grid vs Table toggle for list view (initialized from user default preference)
  const [viewMode, setViewMode] = useDefaultViewMode();

  // Form Data state
  const [formData, setFormData] = useState<Partial<AcademyDiscount>>(emptyDiscount());

  // Assign Student Modal
  const [openAssignModal, setOpenAssignModal] = useState(false);
  const [targetDiscount, setTargetDiscount] = useState<AcademyDiscount | null>(null);
  const [selectedStudentIds, setSelectedStudentIds] = useState<number[]>([]);
  const [assigningStudentIds, setAssigningStudentIds] = useState<number[]>([]);
  const [assignSearchText, setAssignSearchText] = useState('');

  const [alertMsg, setAlertMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    return academyStore.subscribe(() => setTick(t => t + 1));
  }, []);

  const discounts = academyStore.getDiscounts();
  const students = academyStore.getStudents();
  const invoices = academyStore.getInvoices();

  const filteredStudents = useMemo(() => {
    if (!assignSearchText.trim()) return students;
    const q = assignSearchText.toLowerCase().trim();
    return students.filter(s =>
      (s.first_name || '').toLowerCase().includes(q)
      || (s.last_name || '').toLowerCase().includes(q)
      || (s.email || '').toLowerCase().includes(q)
      || (s.phone || '').includes(q)
      || (`${s.first_name} ${s.last_name}`).toLowerCase().includes(q)
    );
  }, [students, assignSearchText]);

  // ── Navigation Helpers ─────────────────────────────────────────────────────

  const openNew = () => {
    setFormData(emptyDiscount());
    setView('form');
  };

  const openEdit = (disc: AcademyDiscount) => {
    setFormData({ ...disc });
    setView('form');
  };

  const backToList = () => {
    setView('list');
    setFormData(emptyDiscount());
  };

  const handleOpenAssignModal = (disc: AcademyDiscount) => {
    setTargetDiscount(disc);
    const assigned = students.filter(s => s.discount_id === disc.id || s.discount_code === disc.code).map(s => s.id);
    setSelectedStudentIds(assigned);
    setAssignSearchText('');
    setOpenAssignModal(true);
  };

  const handleStudentAssignmentToggle = async (studentId: number, checked: boolean) => {
    if (!targetDiscount || assigningStudentIds.includes(studentId)) return;

    const previousSelected = selectedStudentIds;
    setSelectedStudentIds(prev =>
      checked ? [...prev, studentId] : prev.filter(id => id !== studentId)
    );
    setAssigningStudentIds(prev => [...prev, studentId]);

    try {
      const res = await academyStore.assignDiscountToStudent(
        studentId,
        checked ? targetDiscount.id : null
      );
      if (!res.success) {
        setSelectedStudentIds(previousSelected);
        blobticMessage.error(t('common.error'), res.message || t('academy.discounts.assign_error'));
      }
    } catch {
      setSelectedStudentIds(previousSelected);
      blobticMessage.error(t('common.error'), t('academy.discounts.assign_error'));
    } finally {
      setAssigningStudentIds(prev => prev.filter(id => id !== studentId));
    }
  };

  const handleValidate = (): string | null => {
    if (!formData.code?.trim()) return t('academy.discounts.validation.code_required');
    if (!formData.name?.trim()) return t('academy.discounts.validation.name_required');
    return null;
  };

  // GenericPage auto-updates IndexedDB → academyStore refreshes via subscription.
  const handleDataChange = () => {
    setAlertMsg({ type: 'success', text: t('academy.discounts.msg_saved') });
    setTimeout(() => setAlertMsg(null), 3000);
    backToList();
  };


  const handleClear = () => {
    setFormData(emptyDiscount());
  };

  const handleDeleteDiscount = async (disc: AcademyDiscount) => {
    const assignedStudents = students.filter(s => s.discount_id === disc.id || s.discount_code === disc.code);
    const linkedInvoices = invoices.filter(i => i.discount_id === disc.id || i.discount_code === disc.code);
    const isUsed = assignedStudents.length > 0 || linkedInvoices.length > 0;

    if (isUsed) {
      setAlertMsg({
        type: 'error',
        text: t('academy.discounts.msg_blocked_delete', disc.code, disc.name)
      });
      return;
    }

    // boubaker 23/08/2026 - confirmation via le composant générique animé
    const confirmed = await blobticMessage.confirm({
      title: t('common.confirm.title.delete'),
      message: t('academy.discounts.confirm_delete', disc.code),
      severity: 'warning',
      confirmText: t('common.actions.confirm'),
      cancelText: t('btn.cancel')
    });
    if (!confirmed) return;

    const res = await academyStore.deleteDiscount(disc.id);
    if (res.success) {
      blobticMessage.success(t('common.messages.success'), t('academy.discounts.msg_deleted', disc.code));
    } else {
      blobticMessage.error(t('common.error'), res.message || 'Échec de la suppression.');
    }
  };

  const autoAppliedCount = discounts.filter(d => d.is_auto_applied).length;
  const validationRequiredCount = discounts.filter(d => d.requires_validation).length;

  // ─── LIST VIEW ─────────────────────────────────────────────────────────────

  if (view === 'list') {
    return (
      <GenericPage
        title={t('academy.discounts.title')}
        icon={<DiscountIcon />}
        footerEnabled={false}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        onNew={openNew}
        newButtonLabel={t('academy.discounts.add_new')}
      >
        <Box sx={{ pb: 4 }}>
          {/* Notification Banner */}
          {alertMsg && (
            <Alert severity={alertMsg.type} icon={alertMsg.type === 'success' ? <CheckCircleIcon /> : undefined} sx={{ mb: 3, borderRadius: 2 }}>
              {alertMsg.text}
            </Alert>
          )}

          {/* Overview Cards */}
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <Card variant="outlined" sx={{ borderRadius: 3, borderLeft: '6px solid #6366f1' }}>
                <CardContent>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold">
                    {t('academy.discounts.total_count')}
                  </Typography>
                  <Typography variant="h4" color="primary.main" fontWeight="bold">
                    {discounts.length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Card variant="outlined" sx={{ borderRadius: 3, borderLeft: '6px solid #10b981' }}>
                <CardContent>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold">
                    {t('academy.discounts.auto_count')}
                  </Typography>
                  <Typography variant="h4" color="success.main" fontWeight="bold">
                    {autoAppliedCount}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Card variant="outlined" sx={{ borderRadius: 3, borderLeft: '6px solid #f59e0b' }}>
                <CardContent>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold">
                    {t('academy.discounts.validation_count')}
                  </Typography>
                  <Typography variant="h4" color="warning.main" fontWeight="bold">
                    {validationRequiredCount}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* ── VIEW MODE RENDERING (TABLE vs GRID) ── */}
          {viewMode === 'table' ? (
            /* TABLE VIEW USING GenericTable */
            <GenericTable
              columns={[
                {
                  id: 'code',
                  label: t('academy.discounts.code'),
                  minWidth: 120,
                  format: (val: any) => (
                    <Chip label={val} size="small" color="primary" variant="outlined" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }} />
                  )
                },
                {
                  id: 'name',
                  label: t('academy.discounts.name'),
                  minWidth: 220,
                  format: (val: any, row: AcademyDiscount) => (
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">{val}</Typography>
                      <Typography variant="caption" color="text.secondary">{row.description || '—'}</Typography>
                    </Box>
                  )
                },
                {
                  id: 'percentage',
                  label: t('academy.discounts.percentage'),
                  minWidth: 100,
                  format: (val: any) => (
                    <Chip label={`-${val}%`} color="secondary" size="small" sx={{ fontWeight: 'bold' }} />
                  )
                },
                {
                  id: 'is_auto_applied',
                  label: t('academy.discounts.application'),
                  minWidth: 140,
                  format: (val: any) => (
                    val ? (
                      <Chip label={t('academy.discounts.auto_label')} size="small" color="success" variant="outlined" />
                    ) : (
                      <Chip label={t('academy.discounts.manual_label')} size="small" color="default" variant="outlined" />
                    )
                  )
                },
                {
                  id: 'requires_validation',
                  label: t('academy.discounts.validation'),
                  minWidth: 160,
                  format: (_: any, row: AcademyDiscount) => (
                    row.requires_validation ? (
                      <Chip label={`Validation : ${row.validator_role}`} size="small" color="warning" icon={<SecurityIcon />} />
                    ) : (
                      <Chip label={t('academy.discounts.no_validation')} size="small" color="default" variant="outlined" />
                    )
                  )
                },
                {
                  id: 'assigned_students',
                  label: t('academy.discounts.assigned_students'),
                  minWidth: 170,
                  format: (_: any, row: AcademyDiscount) => {
                    const assigned = students.filter(s => s.discount_id === row.id || s.discount_code === row.code);
                    return (
                      <Button
                        size="small"
                        variant="outlined"
                        color="info"
                        startIcon={<PeopleIcon />}
                        onClick={() => handleOpenAssignModal(row)}
                        sx={{ borderRadius: 2 }}
                      >
                        {assigned.length} apprenant(s)
                      </Button>
                    );
                  }
                },
                {
                  id: 'linked_invoices',
                  label: t('academy.discounts.linked_invoices'),
                  minWidth: 130,
                  format: (_: any, row: AcademyDiscount) => {
                    const linked = invoices.filter(i => i.discount_id === row.id || i.discount_code === row.code);
                    return (
                      <Chip label={`${linked.length} facture(s)`} size="small" color={linked.length > 0 ? 'primary' : 'default'} />
                    );
                  }
                }
              ]}
              data={discounts}
              onEdit={(row: AcademyDiscount) => openEdit(row)}
              onDelete={(row: AcademyDiscount) => handleDeleteDiscount(row)}
            />
          ) : (
            /* GRID VIEW */
            <Grid container spacing={2.5}>
              {discounts.map(disc => {
                const assignedStudents = students.filter(s => s.discount_id === disc.id || s.discount_code === disc.code);
                const linkedInvoices = invoices.filter(i => i.discount_id === disc.id || i.discount_code === disc.code);
                const isUsed = assignedStudents.length > 0 || linkedInvoices.length > 0;

                return (
                  <Grid item xs={12} sm={6} md={4} key={disc.id}>
                    <Card
                      variant="outlined"
                      sx={{
                        borderRadius: 3,
                        borderColor: disc.is_auto_applied ? 'success.light' : disc.requires_validation ? 'warning.light' : 'divider',
                        transition: 'transform 0.2s',
                        '&:hover': { boxShadow: 4, transform: 'translateY(-2px)' }
                      }}
                    >
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                          <Chip label={disc.code} size="small" color="primary" variant="outlined" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }} />
                          <Chip label={`-${disc.percentage}%`} color="secondary" size="small" sx={{ fontWeight: 'bold' }} />
                        </Box>

                        <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
                          {disc.name}
                        </Typography>

                        <Typography variant="body2" color="text.secondary" sx={{ minHeight: 40, mb: 2 }}>
                          {disc.description || '—'}
                        </Typography>

                        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, mb: 2, bgcolor: 'action.hover' }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                            <Typography variant="caption" fontWeight="bold">
                              {t('academy.discounts.application')} :
                            </Typography>
                            {disc.is_auto_applied ? (
                              <Chip label={t('academy.discounts.auto_label')} size="small" color="success" variant="outlined" />
                            ) : (
                              <Chip label={t('academy.discounts.manual_label')} size="small" color="default" variant="outlined" />
                            )}
                          </Stack>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="caption" fontWeight="bold">
                              {t('academy.discounts.validation')} :
                            </Typography>
                            {disc.requires_validation ? (
                              <Chip label={disc.validator_role} size="small" color="warning" icon={<SecurityIcon />} />
                            ) : (
                              <Chip label={t('academy.discounts.no_validation')} size="small" color="default" variant="outlined" />
                            )}
                          </Stack>
                        </Paper>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            color="info"
                            startIcon={<PeopleIcon />}
                            onClick={() => handleOpenAssignModal(disc)}
                            sx={{ borderRadius: 2 }}
                          >
                            {assignedStudents.length} apprenant(s)
                          </Button>
                          <Chip label={`${linkedInvoices.length} facture(s)`} size="small" color={linkedInvoices.length > 0 ? 'primary' : 'default'} />
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                          <IconButton size="small" color="primary" onClick={() => openEdit(disc)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <Tooltip title={isUsed ? "Remise utilisée ou attribuée : suppression bloquée" : "Supprimer cette remise"} arrow>
                            <span>
                              <IconButton size="small" color="error" disabled={isUsed} onClick={() => handleDeleteDiscount(disc)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}

          <RootDialogPage
            open={openAssignModal}
            onClose={() => { setOpenAssignModal(false); setAssignSearchText(''); }}
            title={`${t('academy.discounts.btn_assign_students')} — ${targetDiscount?.name ?? ''} (-${targetDiscount?.percentage ?? 0}%)`}
            icon={<PeopleIcon />}
            maxWidth="md"
            fullWidth
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minHeight: 360 }}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <TextField
                  placeholder={t('academy.discounts.assign_search_placeholder')}
                  size="small"
                  fullWidth
                  value={assignSearchText}
                  onChange={(e) => setAssignSearchText(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: assignSearchText ? (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setAssignSearchText('')}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : undefined,
                    sx: { borderRadius: 2 }
                  }}
                />
                <Chip
                  label={t('academy.discounts.assign_assigned_count', String(selectedStudentIds.length), String(students.length))}
                  color="primary"
                  variant="outlined"
                  sx={{ fontWeight: 'bold', flexShrink: 0 }}
                />
              </Stack>

              {filteredStudents.length === 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, opacity: 0.6 }}>
                  <PeopleIcon sx={{ fontSize: 56, mb: 1 }} />
                  <Typography variant="body1" color="text.secondary">
                    {t('academy.discounts.assign_no_results')}
                  </Typography>
                </Box>
              ) : (
                <List
                  disablePadding
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 2,
                    overflow: 'auto',
                    maxHeight: { xs: 'none', sm: 420 },
                    bgcolor: 'background.paper'
                  }}
                >
                  {filteredStudents.map((s) => {
                    const isChecked = selectedStudentIds.includes(s.id);
                    const isAssigning = assigningStudentIds.includes(s.id);
                    return (
                      <ListItem
                        key={s.id}
                        sx={{
                          py: 1.5,
                          px: 2,
                          borderBottom: 1,
                          borderColor: 'divider',
                          bgcolor: isChecked ? 'action.hover' : 'transparent',
                          '&:hover': { bgcolor: isChecked ? 'action.selected' : 'action.hover' }
                        }}
                        disabled={isAssigning}
                        secondaryAction={
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            {isChecked && (
                              <Chip label={targetDiscount?.code} size="small" color="primary" sx={{ height: 22, fontWeight: 'bold' }} />
                            )}
                            <Checkbox
                              edge="end"
                              checked={isChecked}
                              disabled={isAssigning}
                              onClick={() => handleStudentAssignmentToggle(s.id, !isChecked)}
                              disableRipple
                              color="primary"
                            />
                          </Stack>
                        }
                      >
                        <ListItemText
                          primary={
                            <Typography variant="body1" fontWeight={isChecked ? 600 : 400}>
                              {s.first_name} {s.last_name}
                            </Typography>
                          }
                          secondary={
                            <Stack direction="row" spacing={1} sx={{ mt: 0.3 }} flexWrap="wrap">
                              {s.email && <Typography variant="caption" color="text.secondary">{s.email}</Typography>}
                              {s.phone && <Typography variant="caption" color="text.secondary">{s.phone}</Typography>}
                              {s.discount_code && s.discount_code !== targetDiscount?.code && (
                                <Chip label={s.discount_code} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                              )}
                            </Stack>
                          }
                        />
                      </ListItem>
                    );
                  })}
                </List>
              )}
            </Box>
          </RootDialogPage>
        </Box>
      </GenericPage>
    );
  }

  // ─── FORM VIEW (Standard CRUD Page with Top Action Bar & Footer Audit) ────────────

  const isEditing = Boolean(formData.id);
  const formPayload = discountToDto(formData);

  return (
    <GenericPage
      title={isEditing ? t('academy.discounts.title_edit', formData.code) : t('academy.discounts.title_new')}
      icon={<DiscountIcon />}
      footerEnabled={true}
      footerInfo={{
        lastUpdate: formData.last_updt || '-',
        userLib: formData.user_lib || (formData as any).USER_LIB || '-',
      }}
      additionalActions={
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={backToList}
          sx={{ borderRadius: 2 }}
        >
          {t('academy.discounts.back_to_list')}
        </Button>
      }
      crudConfig={{
        entityKey: ENTITY_KEY,
        actionSuffix: ACTION_SUFFIX,
        primaryKey: PRIMARY_KEY,
      }}
      data={formPayload}
      onDataChange={() => handleDataChange()}

      onValidate={handleValidate}
      onClear={handleClear}
    >
      <Paper elevation={3} sx={{ maxWidth: 800, mx: 'auto', p: 4, borderRadius: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <TextField
            label={t('academy.discounts.code')}
            value={formData.code || ''}
            onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
            fullWidth
            required
            disabled={isEditing}
          />

          <TextField
            label={t('academy.discounts.name')}
            value={formData.name || ''}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            fullWidth
            required
          />

          <TextField
            label={t('academy.discounts.percentage')}
            type="number"
            value={formData.percentage ?? 0}
            onChange={(e) => setFormData(prev => ({ ...prev, percentage: Number(e.target.value) }))}
            fullWidth
            required
          />

          <TextField
            select
            label={t('academy.discounts.validation_role')}
            value={formData.validator_role || 'Admin'}
            onChange={(e) => setFormData(prev => ({ ...prev, validator_role: e.target.value as ValidatorRole }))}
            fullWidth
          >
            <MenuItem value="Auto">{t('academy.discounts.role_auto')}</MenuItem>
            <MenuItem value="Planificateur">{t('academy.discounts.role_planner')}</MenuItem>
            <MenuItem value="Admin">{t('academy.discounts.role_admin')}</MenuItem>
          </TextField>

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'action.hover' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(formData.requires_validation)}
                  onChange={(e) => setFormData(prev => ({ ...prev, requires_validation: e.target.checked }))}
                />
              }
              label={t('academy.discounts.requires_val_check')}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(formData.is_auto_applied)}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_auto_applied: e.target.checked }))}
                />
              }
              label={t('academy.discounts.auto_apply_check')}
            />
          </Paper>

          <TextField
            label={t('academy.discounts.desc_label')}
            value={formData.description || ''}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            fullWidth
            multiline
            rows={3}
          />
        </Box>
      </Paper>
    </GenericPage>
  );
};

export default DiscountsPage;
